import { useState, useLayoutEffect } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import { LABELS } from "./constants";
import { Box } from "@cloudscape-design/components-themed";
import Preference from "../UI/Preference";
import { COMMON_LABELS } from "../default-constants";
import { DEFAULT_PAGE_SIZE } from "../../configs/constants";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import Pagination from "../UI/Pagination";
import { Message } from "../../features/api-interfaces";
import { Messages, getMessage } from "../../features/error/messages";

export interface MessageItem {
  no: number;
  messageLabel: string;
  errorOccurredPlace: string;
  errorFileName: string;
}

/**
 * エラーメッセージ作成処理
 * @param messageList メッセージリスト
 * @returns エラーメッセージリスト
 */
export const getMessageItemList = (messageList: Message[]) => {
  // エラーメッセージリスト作成
  let errorMessageList: MessageItem[] = [];

  // エラーメッセージの数分メッセージ作成
  for (const [index, message] of messageList.entries()) {
    const msgId = message.messageId as keyof Messages;
    const messageStr = getMessage(msgId, ...message.messageArgs).label;
    const regex = /(.+?)（不備行：(.+?)、ファイル名：(.+?)）/;
    const match = regex.exec(messageStr);

    // エラー内容をエラーテーブルの形に整形
    const errorData = {
      no: index + 1,
      messageLabel: match ? match[1] : "",
      errorOccurredPlace: match ? match[2] : "",
      errorFileName: match ? match[3] : "",
    } as MessageItem;

    // エラーメッセージリストに追加
    errorMessageList.push(errorData);
  }

  // エラーメッセージの返却
  return errorMessageList;
};

/**
 * エラー発生時のテーブルを表示する。
 *
 * @param props.data エラーデータリスト（MessageItem[]型）
 * @param props.title エラー表示タイトル
 * @returns エラーメッセージ表示テーブル
 */
const ErrorTable = (props: any) => {
  const columnDefinitions = [
    {
      id: "no",
      header: LABELS.NO,
      cell: (e: any) => e.no,
      width: 110,
    },
    {
      id: "messageLabel",
      header: LABELS.ERROR_DETAIL,
      cell: (e: any) => e.messageLabel,
    },
    {
      id: "errorFileName",
      header: LABELS.ERROR_FILENAME,
      cell: (e: any) => e.errorFileName,
    },
    {
      id: "errorOccurredPlace",
      header: LABELS.ERROR_OCCURRED_PLACE,
      cell: (e: any) => e.errorOccurredPlace,
    },
  ];
  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));
  // ページ数設定変数
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [preferences, setPreferences] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    visibleContent: options.map((o) => o.id),
  });

  // ページアイテム初期化
  const [pagedItems, setPagedItems] = useState<Object[]>(
    getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE)
  );
  const [allItems, setAllItems] = useState<Object[]>(props.data);

  useLayoutEffect(() => {
    // エラーデータが存在する場合
    if (props.data !== undefined) {
      // エラー内容をページアイテムにセット
      setPagedItems(getPagedItems(props.data, 1, DEFAULT_PAGE_SIZE));
      setAllItems(props.data);
      setCurrentPageIndex(1);
    }
  }, [props.data]);

  /** 設定変更関数 */
  const handleOnConfirm = (detail: any) => {
    const newPageSize = detail.pageSize;
    setPreferences({
      pageSize: newPageSize,
      visibleContent: detail.visibleContent,
    });
    if (props.data !== undefined) {
      setPagedItems(props.data.slice(0, newPageSize));
      setCurrentPageIndex(1);
    }
  };

  return (
    <CSDTable
      columnDefinitions={columnDefinitions}
      items={pagedItems}
      resizableColumns
      stickyHeader
      header={
        <Box color="text-status-error" variant="h2">
          {props.title}
          {getHeaderCounterText(
            allItems.length,
            pagedItems.length + (currentPageIndex - 1) * preferences.pageSize
          )}
        </Box>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPageIndex}
          filteredItems={allItems}
          setCurrentPageIndex={setCurrentPageIndex}
          pageSize={preferences.pageSize}
          setPagedItems={setPagedItems}
        />
      }
      preferences={
        <Preference
          preferences={preferences}
          options={options}
          handleOnConfirm={handleOnConfirm}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          {COMMON_LABELS.CSD.NO_DATA}
        </Box>
      }
    />
  );
};

export default ErrorTable;
